import { useState } from 'react';
import { Icon } from '@/components/ui';
import { agentApi } from '@/shared/api/agentApi';
import { toast } from '@/components/ui/Toast';

/**
 * פאנל פרטי הליד: כרטיס איש הקשר עם פעולות מהירות, ומתחתיו שדות המידע.
 *
 * השדות מגיעים מ-`lead.fields` (JSON חופשי שנקבע בייבוא), ולכן התצוגה
 * גנרית: כל מפתח הופך לשורה עם כפתור העתקה. אין כאן רשימת שדות קשיחה —
 * ייבוא עם עמודות חדשות מופיע מיד בלי שינוי קוד.
 */

export interface LeadLike {
  id: number;
  name?: string | null;
  phone: string;
  fields?: Record<string, unknown> | null;
}

/** מספר לחיוג בוואטסאפ: ספרות בלבד, עם קידומת ישראל. */
function waNumber(phone: string): string {
  const digits = phone.replace(/\D+/g, '');
  return digits.startsWith('972') ? digits : `972${digits.replace(/^0/, '')}`;
}

async function copy(value: string, label: string) {
  try {
    await navigator.clipboard.writeText(value);
    toast(`${label} הועתק`, 'ok');
  } catch {
    // דפדפן שחוסם clipboard (או הקשר לא-מאובטח) — לא מפילים את המסך.
    toast('ההעתקה נחסמה בדפדפן', 'warn');
  }
}

function CopyButton({ value, label }: { value: string; label: string }) {
  return (
    <button
      type="button"
      className="btn btn-sm"
      onClick={() => void copy(value, label)}
      aria-label={`העתקת ${label}`}
      title={`העתקת ${label}`}
      style={{ padding: '2px 6px' }}
    >
      <Icon name="copy" size={13} />
    </button>
  );
}

export function LeadDetailsPanel({ lead, onDial }: { lead: LeadLike; onDial?: () => void }) {
  // השדות מוחזקים ב-state כדי שהוספה תתעדכן מיד; המקור הוא תמיד התשובה
  // של השרת ולא מיזוג מקומי.
  const [fields, setFields] = useState<Record<string, unknown>>(
    (lead.fields ?? {}) as Record<string, unknown>,
  );
  const [adding, setAdding] = useState(false);
  const [key, setKey] = useState('');
  const [value, setValue] = useState('');
  const [saving, setSaving] = useState(false);

  const rows = Object.entries(fields).filter(([, v]) => v != null && String(v).trim() !== '');

  async function saveField() {
    const k = key.trim();
    const v = value.trim();
    if (!k || !v || saving) return;
    setSaving(true);
    try {
      setFields(await agentApi.addLeadField(lead.id, k, v));
      setKey('');
      setValue('');
      setAdding(false);
      toast('השדה נשמר', 'ok');
    } catch {
      toast('השדה לא נשמר. נסו שוב.', 'danger');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="stack" style={{ gap: 12 }}>
      {/* כרטיס איש הקשר */}
      <div style={{
        background: 'var(--ok-soft)', border: '1px solid var(--line)',
        borderRadius: 'var(--r-3)', padding: 'var(--pad-card)',
      }}>
        <div style={{ fontWeight: 700, fontSize: 'var(--t-lg)' }}>
          {lead.name?.trim() || 'ללא שם'}
        </div>
        <div dir="ltr" style={{ color: 'var(--text-2)', fontSize: 'var(--t-sm)', marginTop: 2 }}>
          {lead.phone}
        </div>

        <div style={{ display: 'flex', gap: 6, marginTop: 10, flexWrap: 'wrap' }}>
          <a
            className="btn btn-sm"
            href={`https://wa.me/${waNumber(lead.phone)}`}
            target="_blank"
            rel="noopener noreferrer"
            title="וואטסאפ"
            aria-label="וואטסאפ"
          >
            <Icon name="chat" size={14} />
          </a>
          <a
            className="btn btn-sm"
            href={`tel:${lead.phone}`}
            title="חיוג מהמכשיר"
            aria-label="חיוג מהמכשיר"
          >
            <Icon name="link" size={14} />
          </a>
          <CopyButton value={lead.phone} label="מספר הטלפון" />
          {onDial && (
            <button type="button" className="btn btn-sm primary" onClick={onDial} title="חיוג" aria-label="חיוג">
              <Icon name="phone" size={14} />
            </button>
          )}
        </div>
      </div>

      {/* שדות המידע */}
      {rows.length > 0 ? (
        <div className="stack" style={{ gap: 6 }}>
          {rows.map(([key, value]) => (
            <div key={key} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <span style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)', minWidth: 90 }}>{key}</span>
              <span style={{ flex: 1, fontSize: 'var(--t-sm)', wordBreak: 'break-word' }}>{String(value)}</span>
              <CopyButton value={String(value)} label={key} />
            </div>
          ))}
        </div>
      ) : (
        <div style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)' }}>
          אין שדות מידע נוספים לליד הזה.
        </div>
      )}

      {/* הוספת שדה מותאם אישית */}
      {adding ? (
        <div className="stack" style={{ gap: 6 }}>
          <input
            className="input"
            value={key}
            onChange={(e) => setKey(e.target.value)}
            placeholder="שם השדה (למשל: עיר)"
            aria-label="שם השדה"
            autoFocus
          />
          <input
            className="input"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); void saveField(); } }}
            placeholder="ערך"
            aria-label="ערך השדה"
          />
          <div style={{ display: 'flex', gap: 6 }}>
            <button
              type="button"
              className="btn btn-sm primary"
              onClick={() => void saveField()}
              disabled={saving || !key.trim() || !value.trim()}
            >
              {saving ? 'שומר…' : 'שמירה'}
            </button>
            <button type="button" className="btn btn-sm" onClick={() => setAdding(false)} disabled={saving}>
              ביטול
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          className="btn btn-sm"
          onClick={() => setAdding(true)}
          style={{ alignSelf: 'flex-start' }}
        >
          הוספת שדה מותאם אישית +
        </button>
      )}
    </div>
  );
}
